import React from "react";
import {offeringSummary, incomeAndExpenses, rentRoll, getRentRoll, incomeAndExpensesProforma, proformaRequirements, incomeAndExpensesAsIS} from "./tables";
import {getOfferingSummaryData, getIncomeAndExpenses, getincomeAndExpensesProforma, getProformaRequirementsData, getincomeAndExpensesAsIS} from "./tables";
import BaseTabComponent from "../UnderwritingMultiTenant/tabs-components/BaseTabComponent";
import {addCommasToNumber, percent} from "../../utils";
import dollarFormat from "../../dollar-format";


let cloneDeep = require('lodash.clonedeep');

const PRINT_MARKER_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 64">
  <path d="M24 2C14.06 2 6 10.06 6 20c0 13.5 15.3 30.8 17.2 32.9.4.45 1 .7 1.6.7.6 0 1.2-.25 1.6-.7C28.7 50.8 42 35.5 42 20 42 10.06 33.94 2 24 2z" fill="#142345"/>
  <polygon points="24,12 27.4,18.9 35,20 29.5,25.3 30.8,32.9 24,29.3 17.2,32.9 18.5,25.3 13,20 20.6,18.9" fill="#ffffff"/>
</svg>
`)}`;

// MT cover: printable area is ~794px @ 96dpi; title sits left of the Summit
// logo in the artwork. Keep in sync with `.cover-bottom-section` insets in
// PrintMultiTenant.scss (roughly 100% − 3% − 20% of page width).
const COVER_PAGE_PRINT_WIDTH_PX = 794;
const COVER_BOTTOM_SECTION_LEFT_PCT = 0.03;
const COVER_BOTTOM_SECTION_RIGHT_PCT = 0.2;
const COVER_PROPERTY_TITLE_MAX_WIDTH_PX = Math.floor(
    COVER_PAGE_PRINT_WIDTH_PX * (1 - COVER_BOTTOM_SECTION_LEFT_PCT - COVER_BOTTOM_SECTION_RIGHT_PCT)
);

const COVER_PROPERTY_TITLE_FONT_MIN = 13;
const COVER_PROPERTY_TITLE_FONT_MAX = 36;
const COVER_PROPERTY_TITLE_FONT_WEIGHT = 800;
/** Futura stack — match `$default-font-family` in `src/_variables.scss` for canvas measurement. */
const COVER_PROPERTY_TITLE_FONT_FAMILY = "'Futura', 'Futura PT', sans-serif";

/**
 * One-line property name on the MT cover: scale font down when the string is
 * long so it stays within the banner width left of the logo (print layout is
 * often `display:none`, so sizing uses a fixed pixel budget + canvas measure).
 */
function MtCoverPropertyName({ text }) {
    const [fontSize, setFontSize] = React.useState(COVER_PROPERTY_TITLE_FONT_MAX);

    React.useLayoutEffect(() => {
        if (typeof document === 'undefined' || !text) {
            return undefined;
        }

        const computeSize = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            const family = COVER_PROPERTY_TITLE_FONT_FAMILY;
            const weight = COVER_PROPERTY_TITLE_FONT_WEIGHT;
            const maxW = COVER_PROPERTY_TITLE_MAX_WIDTH_PX - 6; // padding fudge for PDF engines

            const measure = (sizePx) => {
                ctx.font = `${weight} ${sizePx}px ${family}`;
                return ctx.measureText(text).width;
            };

            if (measure(COVER_PROPERTY_TITLE_FONT_MAX) <= maxW) {
                setFontSize(COVER_PROPERTY_TITLE_FONT_MAX);
                return;
            }

            let lo = COVER_PROPERTY_TITLE_FONT_MIN;
            let hi = COVER_PROPERTY_TITLE_FONT_MAX;
            let best = lo;
            for (let i = 0; i < 22; i++) {
                const mid = (lo + hi) / 2;
                if (measure(mid) <= maxW) {
                    best = mid;
                    lo = mid;
                } else {
                    hi = mid;
                }
            }
            setFontSize(Math.max(COVER_PROPERTY_TITLE_FONT_MIN, Math.floor(best * 10) / 10));
        };

        const schedule = () => {
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(computeSize).catch(computeSize);
            } else {
                computeSize();
            }
        };

        schedule();
        window.addEventListener('beforeprint', schedule);
        return () => window.removeEventListener('beforeprint', schedule);
    }, [text]);

    const lineHeightPx = Math.round(fontSize * (42 / 36));

    return (
        <div className="cover-property-name-wrap">
            <span
                className="bs-line bs-line-2"
                style={{
                    fontSize: `${fontSize}px`,
                    lineHeight: `${lineHeightPx}px`,
                    whiteSpace: 'nowrap',
                }}
            >
                {text}
            </span>
        </div>
    );
}

export default class PrintMultiTenant extends React.Component {
    /**
     * Client-facing row targets for MT rent roll pagination.
     * - First page fits ~21 tenants (title band + tighter row layout).
     * - Last partial pages that include a filler image need a smaller slice.
     *
     * @param {boolean} isFirstRentRollPage First slice includes the RENT ROLL title band.
     * @param {boolean} reservePropertyImage Reserve headroom for filler photo + note.
     */
    static getRentRollRowsPerPage(isFirstRentRollPage = true, reservePropertyImage = false) {
        const FIRST_PAGE_TARGET_ROWS = 20;
        const CONTINUATION_TARGET_ROWS = 20;
        const IMAGE_PAGE_TARGET_ROWS = 10;

        if (reservePropertyImage) {
            return IMAGE_PAGE_TARGET_ROWS;
        }
        return isFirstRentRollPage ? FIRST_PAGE_TARGET_ROWS : CONTINUATION_TARGET_ROWS;
    }

    static buildRentRollPageSlices(rentRollRows) {
        const firstMax = PrintMultiTenant.getRentRollRowsPerPage(true, false);
        const contMax = PrintMultiTenant.getRentRollRowsPerPage(false, false);

        if (!rentRollRows.length) {
            return [];
        }

        const slices = [];
        let offset = 0;
        let take = Math.min(firstMax, rentRollRows.length - offset);
        slices.push(rentRollRows.slice(offset, offset + take));
        offset += take;
        while (offset < rentRollRows.length) {
            take = Math.min(contMax, rentRollRows.length - offset);
            slices.push(rentRollRows.slice(offset, offset + take));
            offset += take;
        }
        return slices;
    }

    constructor(props) {
        super(props);
        this.state = {
            data: {
                offeringSummary: offeringSummary,
                incomeAndExpenses: incomeAndExpenses,
                rentRoll: rentRoll,
                incomeAndExpensesProforma: incomeAndExpensesProforma,
                proformaRequirements : proformaRequirements,
                incomeAndExpensesAsIS : incomeAndExpensesAsIS                  
            },
        }
    }

    async createMap(propertyMapProps) {
        let {default: mapboxgl} = await import('mapbox-gl/dist/mapbox-gl.js');

        mapboxgl.accessToken = 'pk.eyJ1IjoiYW5kcmVlYW1pcmNlYSIsImEiOiJja2lpa25rNzQwYWV0MnNxcGdscDVnbGc3In0.YW3ma8Gr7xlYFsAfLwTL1A';
        let map = new mapboxgl.Map({
            container: 'map',
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [propertyMapProps.LONGITUDE, propertyMapProps.LATITUDE],
            zoom: 12,
            minzoom: 12,
            maxzoom: 12,
        });

        let el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundImage = `url("${PRINT_MARKER_SVG}")`;
        el.style.backgroundSize = '30px 45px';
        el.style.backgroundRepeat = 'no-repeat';
        el.style.width = '30px';
        el.style.height = '45px';

        new mapboxgl.Marker(el).setLngLat([propertyMapProps.LONGITUDE, propertyMapProps.LATITUDE])
            .addTo(map);

        const propertyName = propertyMapProps.NAME;
        new mapboxgl.Popup()
            .setLngLat([propertyMapProps.LONGITUDE, propertyMapProps.LATITUDE])
            .setHTML(propertyName)
            .addTo(map);
    }

    componentDidMount() {
        if (!document.querySelector('#mapboxstyles')) {
            const link = document.createElement('link');
            link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.0.0/mapbox-gl.css?optimize=true';
            link.rel = 'stylesheet';
            link.id = 'mapboxstyles';
            document.head.appendChild(link)
        }
        // Flag the body so the global CSS can hide the Kore phone SDK
        // floating iframe while the proposal print layout is on screen.
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.add('is-print-mode');
        }
        setTimeout(() => this.createMap(this.props.propertyMapProps), 1000);
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined' && document.body) {
            document.body.classList.remove('is-print-mode');
        }
    }

    async componentWillReceiveProps(nextProps) {
        const data = cloneDeep(this.state.data);
        const precomputedValues = BaseTabComponent.precomputeHeavilyUsedValues(nextProps.tabs);
        data.offeringSummary.rows = getOfferingSummaryData(nextProps.tabs, precomputedValues);
        data.incomeAndExpenses = getIncomeAndExpenses(nextProps.tabs, precomputedValues);
        data.rentRoll = getRentRoll(nextProps.tabs, precomputedValues);
        data.incomeAndExpensesProforma = getincomeAndExpensesProforma(nextProps.tabs, precomputedValues);
        data.proformaRequirements = getProformaRequirementsData(nextProps.tabs, precomputedValues);
        data.incomeAndExpensesAsIS = getincomeAndExpensesAsIS(nextProps.tabs, precomputedValues);

        this.setState({data});
    }

    static renderTable(table, extraClass = '') {
        return (
            <div className={`table ${extraClass}`.trim()}>
                <div className='title'>{table.name}</div>
                {table.rows ?
                    table.rows.map((row, index) => {
                        return (
                            <div key={index} className={`row${index % 2 !== 0 ? ' white' : ''}`}>
                                <div>{row.label}</div>
                                <div>{row.value}</div>
                            </div>
                        )
                    })
                    : null
                }
            </div>
        );
    }
    componentDidUpdate(prevProps, prevState) {
        const { pageNo } = this.props;
        if (pageNo === 3 && pageNo !== prevProps.pageNo) {
            const thirdPage = document.getElementById('');
            if (thirdPage) {
                const redBox = document.createElement('');
                redBox.classList.add('red-box');
                thirdPage.appendChild(redBox);
            }
        }
    }
    static renderTableMatrix(table, tableClassName, allowFooter, isProformaCheck, extraClass = '', rentRollRowsPerPage = null) {
        const rrChunk =
            tableClassName === 'rent-roll'
                ? (rentRollRowsPerPage != null ? rentRollRowsPerPage : PrintMultiTenant.getRentRollRowsPerPage(true))
                : null;
        return (
            <div className={`table ${tableClassName === 'income-expenses' && !isProformaCheck ? 'big-margin ' : ''}${tableClassName} ${extraClass}`.trim()}>
                {table.header ?
                    <div className='row table-header'>
                        {table.header.map((cell, index) => {
                            return (
                                <div key={index} className='cell first-letter'>{cell}</div>
                            )
                        })}
                    </div>
                    : null
                }
                {table.rows.map((row, index) => {
                    return (
                        <div key={index} className={`row${index % 2 !== 0 ? ' white' : ''} ${table.rows.length - 1 === index ? 'last-row' : ''}`}>
                            {row.valueCells.map((cell, cellIndex) => {
                                return (
                                    <div key={`${index}-${cellIndex}`} className='cell'>{cell.value}</div>
                                )
                            })}
                        </div>
                    )
                })}
                {(table.footer && tableClassName === 'rent-roll' && allowFooter) || (table.footer && tableClassName === 'income-expenses')  ?
                    <div className={`${tableClassName === 'rent-roll' ? 'column' : 'row'} table-footer`}>
                        {table.footer.map((elem, index) => {
                            if (Array.isArray(elem)) {
                                return (
                                    <div key={index} className='row'>
                                        {elem.map((cell, cellIndex) => {
                                            return (
                                                <div key={`${index}-${cellIndex}`} className='cell'>{cell}</div>
                                            )
                                        })}
                                    </div>
                                )
                            }
                            return (
                                <div key={index} className={`cell ${index === 0 ? 'first-letter' : 'value'}`}>{elem}</div>
                            )
                        })}
                    </div>
                    : null
                }
            </div>
        )
    }

    static getCoverImage(props) {
        if (props.isCategoryOffice) {
            return '/img/Office-STNL-and-office-MT-page-1.jpeg';
        }
        return '/img/Retail-mt-proforma-and-Retail-mt-page-1.jpeg';
    }

    /**
     * Client rule for MT rent-roll filler image:
     * - show only on the last page
     * - only when the last page has enough space (1-10 tenant rows)
     * - never show on fuller pages (11+ rows) or earlier pages
     */
    static shouldShowRentRollPropertyImage(propertyImage, rowsOnCurrentPage, pageIndex, pagesLength) {
        if (!propertyImage) return false;
        if (pageIndex !== pagesLength - 1) return false;
        if (rowsOnCurrentPage > 10) return false;
        return true;
    }

    static renderContent(pageNo, props, data, pagesData = [], index = 0, agents = [], isRentRollPage = false, rentRollRowsPerPage = null) {
        const isProformaCheck = props.tabs['pricing-tab'].tables['pricing-analysis'].rows['proforma-checkbox'].valueCells[1].value == true;
        const name = props.tabs['property-information-tab'].tables['general-information'].rows['property-name'].value;
        const address = props.tabs['property-information-tab'].tables['general-information'].rows['address'].value;
        const city = props.tabs['property-information-tab'].tables['general-information'].rows['city'].value;
        const state = props.tabs['property-information-tab'].tables['general-information'].rows['state'].value;
        const zip = props.tabs['property-information-tab'].tables['general-information'].rows['zip'].value;
        const photo = arc.path.media + props.propertyImage;
        const precomputedValues = BaseTabComponent.precomputeHeavilyUsedValues(props.tabs);
        const aggressive = props.tabs['pricing-tab'].tables['pricing-sensitivity-matrix'].rows['value'].valueCells[1].getComputedValue(props.tabs, precomputedValues).internalValue || '0';
        const anticipated = props.tabs['pricing-tab'].tables['pricing-sensitivity-matrix'].rows['value'].valueCells[2].getComputedValue(props.tabs, precomputedValues).internalValue || '0';
        const conservative = props.tabs['pricing-tab'].tables['pricing-sensitivity-matrix'].rows['value'].valueCells[3].getComputedValue(props.tabs, precomputedValues).internalValue || '0';
        const aggressive_cap = props.tabs['pricing-tab'].tables['pricing-sensitivity-matrix'].rows['cap-rate'].valueCells[1].getComputedValue(props.tabs, precomputedValues).displayValue || '0';
        const anticipated_cap = props.tabs['pricing-tab'].tables['pricing-sensitivity-matrix'].rows['cap-rate'].valueCells[2].getComputedValue(props.tabs, precomputedValues).displayValue || '0';
        const conservative_cap = props.tabs['pricing-tab'].tables['pricing-sensitivity-matrix'].rows['cap-rate'].valueCells[3].getComputedValue(props.tabs, precomputedValues).displayValue || '0';
        const yearBuilt = props.tabs['marketing-tab'].tables['offering-summary'].rows['year-built'].getComputedValue(props.tabs, precomputedValues).internalValue || '-';
        let noiValue = props.tabs['pricing-tab'].tables['net-operating-income'].rows['net-operating-income'].valueCells[2].getComputedValue(props.tabs, precomputedValues).internalValue;
        const noi = noiValue ? (dollarFormat(noiValue)) : '-';
        const lot_size = props.tabs['marketing-tab'].tables['offering-summary'].rows['lot-size'].getComputedValue(props.tabs, precomputedValues).internalValue || '0';
        const Strengths = props.proposalData.STRENGTHS || '';
        const challenges = props.proposalData.CHALLENGES || '';
        const buyerPool = props.proposalData.BUYER_POOL || '';
        const qualifications = props.proposalData.QUALIFICATIONS || '';
        const recommendations = props.proposalData.RECOMMENDATIONS || '';
        const Occupancy = percent(props.tabs['marketing-tab'].tables['offering-summary'].rows['occupancy'].getComputedValue(props.tabs, precomputedValues).internalValue) || "-";
        const leaseTypes = data.rentRoll.rows || '';
        const analysisDate = props.tabs['pricing-tab'].tables['pricing-analysis'].rows['pricing-analysis'].valueCells[1].value || 'XX/XX/XXXX';
        // Treat the Value Analysis strip as "Lease Types: NNN" whenever every
        // tenant is on an NNN-family lease (e.g. NNN, NNN+1%AF, NNN+5% Ctrl
        // CAM Cap, NNN+Net MGT). Previously this required an exact "NNN"
        // string match, which caused centers like Harvest Hill to render
        // "Occupancy" instead — the client's reference shows Lease Types.
        let LeaseCheck = false;
        if (leaseTypes.length > 0) {
            const allNNN = leaseTypes.every((row) => {
                if (!row || !Array.isArray(row.valueCells) || row.valueCells.length < 12) return false;
                const leaseType = (row.valueCells[11] && row.valueCells[11].value) || '';
                return typeof leaseType === 'string' && leaseType.trim().toUpperCase().startsWith('NNN');
            });
            if (allNNN) {
                LeaseCheck = true;
            }
        }

        const coverImage = PrintMultiTenant.getCoverImage(props);
        
        switch (pageNo) {
            case 1:
                return (
                    <div className="page cover-page">
                        <div className='content cover-page-content'>
                            <img src={coverImage} className="cover-img" alt="" />
                            <div className='bottom-section cover-bottom-section'>
                                <div className='bs-line bs-line-1'>Pricing Analysis & Marketing Proposal</div>
                                <MtCoverPropertyName text={name || ''} />
                                <div className='bs-line bs-line-3'>{`${city}, ${state}`}</div>
                            </div>
                        </div>
                    </div>
                )
            case 2:
                if (isProformaCheck) {
                    return (
                        <div>
                            <div>
                            <div className='name'>{name}</div>
                                <div className='address'>{address}</div>
                                <div className='address'>{`${city}, ${state} ${zip}`}</div>
                            </div>
                        <div className='content'>
                            <div className='left-container summary-style'>
                                {PrintMultiTenant.renderTable(data.offeringSummary)}
                            </div>
                            <div className='right-container' style={{marginTop: "0px"}}>
                            {PrintMultiTenant.renderTableMatrix(data.proformaRequirements, 'income-expenses proforma', false, isProformaCheck)}
                            </div>
                        </div>
                            <div className='PrintSingleTenant_coverimage'>
                       <img src={photo} alt=''/>
                    </div>
                        </div>
                    );   
                }
                return (
                    <React.Fragment>
                        <div className='name'>{name}</div>
                        <div className='address'>{address}</div>
                        <div className='address'>{`${city}, ${state} ${zip}`}</div>
                        <div className='content offering-income-layout'>
                            <div className='left-container'>
                                {PrintMultiTenant.renderTable(data.offeringSummary, 'offering-summary-table')}
                                <div id='map'/>
                            </div>
                            <div className='right-container'>
                                {PrintMultiTenant.renderTableMatrix(data.incomeAndExpenses, 'income-expenses', false, true, 'income-expenses-table')}
                            </div>
                        </div>
                    </React.Fragment>
                ); 
            case 4: {
                const vaLocationSuffix = [city, state].filter(Boolean).join(', ');
                const vaBannerText = vaLocationSuffix ? `${name} - ${vaLocationSuffix}` : name;
                return (
                    <div className="va-page">
                        <div className="va-header">
                            <div className="va-property-label">{vaBannerText}</div>
                        </div>
                        <div className="va-top">
                            <div className="va-title">Value Analysis</div>
                        </div>
                        <div className="va-curve-section">
                            <img src="/img/va-curve.JPG" alt="" />
                        </div>
                        <div className="va-pricing">
                            <div className="va-price-item">
                                <div className="va-price-label">Conservative</div>
                                <div className="va-price-value">${addCommasToNumber(conservative)}</div>
                                <div className="va-price-cap">{conservative_cap} Cap</div>
                            </div>
                            <div className="va-price-item">
                                <div className="va-price-label">Anticipated</div>
                                <div className="va-price-value">${addCommasToNumber(anticipated)}</div>
                                <div className="va-price-cap">{anticipated_cap} Cap</div>
                            </div>
                            <div className="va-price-item">
                                <div className="va-price-label">Aggressive</div>
                                <div className="va-price-value">${addCommasToNumber(aggressive)}</div>
                                <div className="va-price-cap">{aggressive_cap} Cap</div>
                            </div>
                        </div>
                        <div className="va-data-strip">
                            <div className="va-data-item">
                                <img src="/img/va-icon-calendar.JPG" alt="" />
                                <div className="va-data-label">Year Built</div>
                                <div className="va-data-value">{yearBuilt}</div>
                            </div>
                            <div className="va-data-item">
                                <img src="/img/va-icon-dollar.JPG" alt="" />
                                <div className="va-data-label">NOI</div>
                                <div className="va-data-value">{addCommasToNumber(noi)}</div>
                            </div>
                            <div className="va-data-item">
                                <img src="/img/va-icon-clipboard.JPG" alt="" />
                                <div className="va-data-label">{LeaseCheck === true ? "Lease Types" : "Occupancy"}</div>
                                <div className="va-data-value">{LeaseCheck == true ? "NNN" : Occupancy}</div>
                            </div>
                            <div className="va-data-item">
                                <img src="/img/va-icon-arrow.JPG" alt="" />
                                <div className="va-data-label">Lot Size</div>
                                <div className="va-data-value">{lot_size} Acres</div>
                            </div>
                        </div>
                        <div className="va-bottom">
                            <div className="va-col">
                                <div className="va-section">
                                    <div className="va-section-header">
                                        <img src="/img/va-icon-strengths.JPG" alt="" />
                                        <div className="va-section-title">Strengths</div>
                                    </div>
                                    <ul>{Strengths.split('\n').splice(0, 3).map((res, i) => <li key={i}>{res}</li>)}</ul>
                                </div>
                                <div className="va-section">
                                    <div className="va-section-header">
                                        <img src="/img/va-icon-challenges.JPG" alt="" />
                                        <div className="va-section-title">Challenges</div>
                                    </div>
                                    <ul>{challenges.split('\n').splice(0, 3).map((res, i) => <li key={i}>{res}</li>)}</ul>
                                </div>
                                <div className="va-section">
                                    <div className="va-section-header">
                                        <img src="/img/va-icon-buyerpool.JPG" alt="" />
                                        <div className="va-section-title">Buyer Pool</div>
                                    </div>
                                    <ul>{buyerPool.split('\n').splice(0, 3).map((res, i) => <li key={i}>{res}</li>)}</ul>
                                </div>
                            </div>
                            <div className="va-col">
                                <div className="va-section">
                                    <div className="va-section-header">
                                        <img src="/img/va-icon-qualifications.JPG" alt="" />
                                        <div className="va-section-title">Qualifications</div>
                                    </div>
                                    <ul>{qualifications.split('\n').splice(0, 5).map((res, i) => <li key={i}>{res}</li>)}</ul>
                                </div>
                                <div className="va-section">
                                    <div className="va-section-header">
                                        <img src="/img/va-icon-recommendations.JPG" alt="" />
                                        <div className="va-section-title">Recommendations & Timing</div>
                                    </div>
                                    <ul>{recommendations.split('\n').splice(0, 2).map((res, i) => <li key={i}>{res}</li>)}</ul>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }
            case 3:
                return isProformaCheck ?
                (<div>
                    {index == 0 ? (
                                <div className="title-container" style={{ fontWeight: 'bold' }}>
                                    <h2>
                                    <h2 className="title first-letter">INCOME &</h2>
                                    <h2 className="title first-letter" style= {{marginLeft: '115px', marginTop: '-68px'}}>EXPENSES</h2>
                                    </h2>
                                </div>
                            ) : null}
                    <div className='content'>
                            
                        <div style={{width: "47%", marginRight: "20px"}}>
                        {PrintMultiTenant.renderTableMatrix(data.incomeAndExpensesAsIS, 'income-expenses', false, true)}
                        </div>
                        <div style={{width: "52%"}}>
                        {PrintMultiTenant.renderTableMatrix(data.incomeAndExpensesProforma, 'income-expenses', false, true)}
                        </div>
                    </div>
                    </div>) : null;
            default: {
                data.rentRoll.rows = data.rentRoll.rows.map(row => {
                    row.valueCells.splice(-2, 1);
                    if (row.valueCells.length < data.rentRoll.header.length) {
                        row.valueCells = row.valueCells.concat(Array(data.rentRoll.header.length - row.valueCells.length).fill({value: "-"}));
                    }
                    return row;
                });
                data.rentRoll.footer = data.rentRoll.footer.map((footer, ind) => {
                    if (ind === 0) {
                        const percentage = parseFloat(footer[3].replace("%", ""));
                        const roundedPercent = Math.round(percentage * 100)/100;
                        const formattedValue = roundedPercent.toFixed(2) + "%";
                        footer[3] = formattedValue;
                    }
                    return footer;
                });
                const rrChunk = rentRollRowsPerPage != null ? rentRollRowsPerPage : PrintMultiTenant.getRentRollRowsPerPage(true);
                const showRentRollImage =
                    isRentRollPage &&
                    PrintMultiTenant.shouldShowRentRollPropertyImage(
                        props.propertyImage,
                        data.rentRoll.rows.length,
                        index,
                        pagesData.length
                    );
                return (
                    <React.Fragment>
                         {index == 0 ? (
                                <div className="title-container title-container--rent-roll" style={{ fontWeight: 'bold', alignItems: 'start', textAlign: 'start' , display: 'flex', justifyContent: 'start', height: '50px'}}>
                                    <h2 className="title">RENT ROLL</h2>
                                </div>
                            ) : null}
                    <div className={`content row${showRentRollImage ? ' has-filler-image' : ''}`}>
                        {PrintMultiTenant.renderTableMatrix(data.rentRoll, 'rent-roll', pagesData.length - 1 === index, true, '', rrChunk)}
                        {pagesData.length - 1 === index ? <p style = {{fontSize: "10px", fontWeight: "bold", marginTop:"3px"}}>*Analysis Start Date is {analysisDate} and Takes Into Consideration a 1 Year Hold From This Date</p> : null}

                        {showRentRollImage ?
                            <div className='property-image'>
                                <img src={photo} alt='' />
                            </div>
                            :
                            null
                        }
                    </div>
                    </React.Fragment>
                );
            }
        }
    }

    static renderPage(pageNo, props, data, agents, pagesData = [], index = 0, isRentRollPage = false, rentRollRowsPerPage = null) {
        const name = props.tabs['property-information-tab'].tables['general-information'].rows['property-name'].value;
        const city = props.tabs['property-information-tab'].tables['general-information'].rows['city'].value;
        const state = props.tabs['property-information-tab'].tables['general-information'].rows['state'].value;
        const locationSuffix = [city, state].filter(Boolean).join(', ');
        const bannerText = locationSuffix ? `${name} - ${locationSuffix}` : name;
        const pageClassName = isRentRollPage ? 'page page--rent-roll' : 'page';
        return (
            <div className={pageClassName}>
                <div className='property-label'>{bannerText}</div>
                {PrintMultiTenant.renderContent(pageNo, props, data, pagesData, index, [], isRentRollPage, rentRollRowsPerPage)}
                {PrintMultiTenant.renderFooter(agents)}
            </div>
        );
    }

    static renderFooter(agents, className = 'footer') {
        const visibleAgents = (agents || []).filter(a => a && (a.name || a.phone || a.email));
        const hasAgents = visibleAgents.length > 0;
        const modifier = visibleAgents.length >= 2 ? 'multiple' : 'single';
        return (
            <div className={className}>
                {hasAgents && (
                    <div className={`${className}-agents ${modifier}`}>
                        {visibleAgents.slice(0, 2).map((agent, idx) => {
                            const contactParts = [];
                            if (agent.phone) contactParts.push(agent.phone);
                            if (agent.email) contactParts.push(agent.email);
                            return (
                                <div key={idx} className={`${className}-agent`}>
                                    <span className={`${className}-agent-name`}>{agent.name}</span>
                                    {contactParts.length > 0 && (
                                        <span className={`${className}-agent-contact`}>
                                            {contactParts.map((part, i) => (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className={`${className}-agent-sep`}>|</span>}
                                                    <span>{part}</span>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={`${className}-logo`}>
                    <img src='/img/inline-light-logo.png' alt=''/>
                </div>
            </div>
        );
    }

    render() {
        const isProformaCheck = this.props.tabs['pricing-tab'].tables['pricing-analysis'].rows['proforma-checkbox'].valueCells[1].value == true;
        const data = this.state.data;
        const agents = this.props.agents;
        const rentRollRows = data.rentRoll.rows || [];
        const firstPageSize = PrintMultiTenant.getRentRollRowsPerPage(true, false);
        const continuationPageSize = PrintMultiTenant.getRentRollRowsPerPage(false, false);
        const pageSlices = PrintMultiTenant.buildRentRollPageSlices(rentRollRows);
        const pagesData = [];

        if (rentRollRows.length === 0) {
            pagesData.push({
                ...data,
                rentRoll: {...data.rentRoll, rows: []},
            });
        } else {
            pageSlices.forEach((slice) => {
                pagesData.push({
                    ...data,
                    rentRoll: {
                        ...data.rentRoll,
                        rows: slice,
                    },
                });
            });
        }

        return (    
            <div className='PrintMultiTenant'>  
                {PrintMultiTenant.renderContent(1, this.props, data)}
                {PrintMultiTenant.renderPage(2, this.props, data, agents)}
                {isProformaCheck ? PrintMultiTenant.renderPage(3, this.props, data, agents) : null}
                {pagesData.map((pageData, index) => (
                    <React.Fragment key={index}>
                        {PrintMultiTenant.renderPage(
                            index + 5,
                            this.props,
                            pageData,
                            agents,
                            pagesData,
                            index,
                            true,
                            index === 0 ? firstPageSize : continuationPageSize
                        )}
                    </React.Fragment>
                ))}
                {PrintMultiTenant.renderContent(4, this.props, data, [], 0, agents, false, null)}
                {
                    this.props.staticImages.map((img, index) => {
                        return (<React.Fragment key={index}>
                        <div className="page" style={{ height: 'auto', padding: 0 }}>
                                <div className='content' style={{ position: 'relative', padding: 0 }}>
                                    <img src={img}></img>
                                </div>
                            </div>
                        </React.Fragment>)
                    })
                }
                
            </div>
        )
    }
}
